import useProfile from "@/hooks/use-profile.ts";
import { useCallback, useEffect, useMemo } from "react";
import { useColumn } from "@/hooks/use-column";
import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import { getColumns } from "./columns";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { Title } from "rizzui";
import ModalButton from "@/app/shared/modal-button";
import { IUnitTransaction } from "@/utilities/types/UnitTransaction";
import UnitTransactionRepository from "@/utilities/repositories/UnitTransaction";
import CreateEditUnitTransaction from "../create-edit/create-edit-unit-transactions";
import ProjectRepository from "@/utilities/repositories/Project";
import { Link, useParams } from "react-router-dom";
import { Roles } from "@/utilities/types/Users.ts";
import { backPrev } from "@/utilities/functions";
import { routes } from "@/config/routes";

export default function UnitTransactionsTable() {
  const { request: removeUnitType } = useApi<IResponseData<IUnitTransaction>>({
    request: UnitTransactionRepository.deleteUnitTransaction,
    enableToast: true,
  });

  const {
    request: getDetailProjectById,
    response: projectDetailData,
    loading: getDetailProjectByIdLoading,
  } = useApi({
    request: ProjectRepository.getProjectById,
  });
  const { id } = useParams();
  const { profile } = useProfile();

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleReset,
    handleChangePageSize,
    updateFilter,
  } = useTableServer<IUnitTransaction>({
    server: {
      request: projectDetailData?.data?.slug
        ? UnitTransactionRepository.getAllUnitTransactions
        : null,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
    initialFilterState: { projectSlug: projectDetailData?.data?.slug || "" },
  });

  const onDeleteItem = useCallback(
    async (id: string) => {
      await removeUnitType(id);
      handlePaginate(currentPage); // Recall API
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handlePaginate, currentPage]
  );
  const { openModal } = useModal();
  const onEditItem = (row: IUnitTransaction) => {
    openModal({
      view: (
        <CreateEditUnitTransaction
          refetchData={() => handlePaginate(currentPage)}
          data={row}
        />
      ),
      customSize: "500px",
    });
  };

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        onEditItem,
        onDeleteItem,
        role: profile?.role ?? "",
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditItem, onDeleteItem, JSON.stringify(tableData)]
  );

  useEffect(() => {
    if (projectDetailData?.data?.slug) {
      updateFilter("projectSlug", projectDetailData?.data?.slug || "");
    }
  }, [projectDetailData?.data?.slug]);

  useEffect(() => {
    getDetailProjectById(id);
  }, []);

  const { visibleColumns } = useColumn(columns);
  //TODO: Uncomment filter and test when BE already filter
  return (
    <div className="mt-4">
      <div className="mb-4 w-full flex flex-wrap items-center justify-between">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          {typeof projectDetailData?.data?.name === "object"
            ? projectDetailData?.data?.name?.en
            : projectDetailData?.data?.name}
          {projectDetailData?.data ? "'s" : ""} Unit Transactions
        </Title>
        <Link to={backPrev(routes.project.listing)} className={"mr-2 ml-auto"}>
          <span
            className={
              "px-4 py-2 inline-block border border-solid border-black rounded-md"
            }
          >
            Back
          </span>
        </Link>
        {profile?.role !== Roles.AGENCY && (
          <ModalButton
            view={
              <CreateEditUnitTransaction
                refetchData={handleReset}
                projectId={projectDetailData?.data?.id || ""}
              />
            }
            label="Add Unit Transaction"
            customSize="500px"
            className="mt-0 w-auto"
          />
        )}
      </div>

      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading || getDetailProjectByIdLoading}
        showLoadingText={true}
        // @ts-ignore
        columns={visibleColumns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
